import { useState, useEffect, useCallback } from 'react';
import { App } from '@capacitor/app';
import { Task } from '@/types/task';
import {
  isNativePushSupported,
  getNativePermissionStatus,
  requestNativePermission,
} from '@/lib/pushNotifications';

type NotificationPermission = 'default' | 'granted' | 'denied';

interface ScheduledNotification {
  taskId: string;
  timeoutId: NodeJS.Timeout;
}

// Global map to track scheduled notifications (persists across hook instances)
const scheduledNotifications = new Map<string, NodeJS.Timeout>();

function mapNativeToPermission(state: string): NotificationPermission {
  if (state === 'granted') return 'granted';
  if (state === 'denied') return 'denied';
  return 'default';
}

export function useNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const refreshPermission = useCallback(() => {
    if (!isNativePushSupported()) return;
    getNativePermissionStatus()
      .then((status) => setPermission(mapNativeToPermission(status)))
      .catch(() => setPermission('default'));
  }, []);

  // Check if notifications are supported and get current permission
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (isNativePushSupported()) {
      setIsSupported(true);
      getNativePermissionStatus()
        .then((status) => {
          setPermission(mapNativeToPermission(status));
        })
        .catch(() => setPermission('default'))
        .finally(() => setIsLoading(false));

      // Refresh permission when app returns from foreground (e.g. user enabled in Settings)
      const listener = App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) refreshPermission();
      });
      return () => {
        listener.then((l) => l.remove());
      };
    }

    if ('Notification' in window) {
      setIsSupported(true);
      setPermission(Notification.permission as NotificationPermission);
    }
    setIsLoading(false);
  }, [refreshPermission]);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;

    if (isNativePushSupported()) {
      try {
        const granted = await requestNativePermission();
        setPermission(granted ? 'granted' : 'denied');
        return granted;
      } catch (err) {
        console.error('Error requesting native notification permission:', err);
        return false;
      }
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result as NotificationPermission);
      return result === 'granted';
    } catch (err) {
      console.error('Error requesting notification permission:', err);
      return false;
    }
  }, [isSupported]);

  // Send a notification immediately (Web Notification API - not available on native iOS)
  const sendNotification = useCallback((
    title: string,
    options?: NotificationOptions
  ): Notification | null => {
    if (!isSupported || permission !== 'granted') return null;
    // Native iOS uses APNs for push; local notifications need @capacitor/local-notifications
    if (isNativePushSupported()) return null;

    try {
      const notification = new Notification(title, {
        icon: '/favicon-32.png',
        badge: '/favicon-32.png',
        ...options,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return notification;
    } catch (err) {
      console.error('Error sending notification:', err);
      return null;
    }
  }, [isSupported, permission]);

  // Send timer complete notification
  const sendTimerCompleteNotification = useCallback((taskName?: string) => {
    return sendNotification(
      '⏰ Focus session complete!',
      {
        body: taskName 
          ? `Great work on "${taskName}". Time to take a break.`
          : 'Time to take a well-deserved break.',
        tag: 'timer-complete',
        requireInteraction: true,
      }
    );
  }, [sendNotification]);

  // Send break over notification
  const sendBreakOverNotification = useCallback((taskName?: string) => {
    return sendNotification(
      '☕ Break is over!',
      {
        body: taskName 
          ? `Ready to get back to "${taskName}"?`
          : 'Ready to get back to focus mode?',
        tag: 'break-over',
        requireInteraction: true,
      }
    );
  }, [sendNotification]);

  // Send task due notification
  const sendTaskDueNotification = useCallback((taskName: string) => {
    return sendNotification(
      '📅 Task due soon',
      {
        body: `"${taskName}" is due in 30 minutes`,
        tag: `task-due-${taskName}`,
        requireInteraction: false,
      }
    );
  }, [sendNotification]);

  // Schedule a notification for a task's due time
  const scheduleTaskDueNotification = useCallback((task: Task, minutesBefore: number = 30) => {
    if (!isSupported || permission !== 'granted') return;
    if (!task.due_time || !task.day) return;

    // Cancel any existing notification for this task
    cancelScheduledNotification(task.id);

    // Parse the due time
    const [hours, minutes] = task.due_time.split(':').map(Number);
    const dueDate = new Date(task.day);
    dueDate.setHours(hours, minutes, 0, 0);

    // Calculate notification time (X minutes before due)
    const notifyAt = new Date(dueDate.getTime() - minutesBefore * 60 * 1000);
    const now = new Date();
    const delay = notifyAt.getTime() - now.getTime();

    // Only schedule if the notification time is in the future
    if (delay > 0) {
      const timeoutId = setTimeout(() => {
        sendTaskDueNotification(task.content);
        scheduledNotifications.delete(task.id);
      }, delay);

      scheduledNotifications.set(task.id, timeoutId);
    }
  }, [isSupported, permission, sendTaskDueNotification]);

  // Cancel a scheduled notification
  const cancelScheduledNotification = useCallback((taskId: string) => {
    const timeoutId = scheduledNotifications.get(taskId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      scheduledNotifications.delete(taskId);
    }
  }, []);

  // Cancel all scheduled notifications
  const cancelAllScheduledNotifications = useCallback(() => {
    scheduledNotifications.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    scheduledNotifications.clear();
  }, []);

  return {
    // Permission state
    permission,
    isSupported,
    isEnabled: permission === 'granted',
    isLoading,
    
    // Permission request
    requestPermission,
    
    // Notification methods
    sendNotification,
    sendTimerCompleteNotification,
    sendBreakOverNotification,
    sendTaskDueNotification,
    
    // Scheduling methods
    scheduleTaskDueNotification,
    cancelScheduledNotification,
    cancelAllScheduledNotifications,
  };
}
